import { useCallback, useRef } from 'react'

import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { evaluateQualification } from './qualification'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { PracticeType } from '@/constants'
import {
  useNavigateWithPersistParams,
  useReselector,
} from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { NumberOfEmployees } from '@/features/user/signup/your-practice/index'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  canOptIntoPreviousYearTaxes,
  selectCurrentAnnualTaxDetails,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import type { SelectedInstitution } from '@/features/user/signup/about-you/bank-compatibility.types'
import { fetchSignupPlans } from '@/features/user/signup/plans/signupPlans.actions'
import { showsChooser } from '@/features/user/signup/plans/chooserVisibility'
import { recordChooserDecision } from '@/features/user/signup/plans/chooserDecision'
import { useNewCatalogSignup } from '@/features/user/signup/plans/useNewCatalogSignup'

export const useSubmitYourPractice = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const financialProfile = useReselector(getFinancialProfile)

  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)
  // Read through a ref so a flag that resolves while the profile saves is the one that routes.
  const catalogFlag = useNewCatalogSignup()
  const catalogFlagRef = useRef(catalogFlag)
  catalogFlagRef.current = catalogFlag

  return useCallback(
    async ({
      taxEntityType,
      businessEntity,
      accountsSeparate,
      hasMultipleOwners,
      hasPhysicalInventory,
      fileInternationalTaxes,
      approximatePracticeStartDate,
      scorpEntireYear,
      numberOfEmployees,
      selectedInstitutions,
    }: {
      taxEntityType: TAX_ENTITY_TYPES | 'other'
      businessEntity?: string
      accountsSeparate?: string
      willingAccountsSeparate?: string
      hasMultipleOwners: boolean | string | null
      hasPhysicalInventory: boolean | string | null
      fileInternationalTaxes?: boolean | string | null
      approximatePracticeStartDate: string
      scorpEntireYear: boolean | string | null
      numberOfEmployees: string
      selectedInstitutions?: SelectedInstitution[]
    }) => {
      // Routing S corps and groups depends on the flag, so an unresolved one decides nothing.
      if (catalogFlagRef.current === null) return

      const verdict = evaluateQualification({
        taxEntityType,
        businessEntity,
        hasMultipleOwners,
        hasPhysicalInventory,
        fileInternationalTaxes,
        scorpEntireYear,
        numberOfEmployees,
      })

      if (verdict.treatment === 'disqualified') {
        const reasons = new Set(verdict.reasons)
        navigate(SIGNUP_PATHS.disqualified, {
          ...(reasons.has('international_taxes') && {
            hasInternationalTaxes: 'true',
          }),
          ...(reasons.has('multiple_owners') && { multipleOwners: 'true' }),
          ...(reasons.has('c_corp') && { ccorp: 'true' }),
          ...(reasons.has('scorp_partial_year') && { scorpEntireYear: 'true' }),
          ...(reasons.has('physical_inventory') && {
            hasPhysicalInventory: 'true',
          }),
          ...(reasons.has('ten_or_more_employees') && {
            tooManyEmployees: 'true',
          }),
        })
      } else if (verdict.treatment === 'waitlist') {
        navigate(SIGNUP_PATHS.waitlist)
      } else if (verdict.treatment === 'consult') {
        navigate(SIGNUP_PATHS.consult)
      } else {
        const taxEntityValue =
          taxEntityType === 'other'
            ? TAX_ENTITY_TYPES.schedule_c
            : taxEntityType

        await updateFinancialProfile(financialProfile?.id, {
          accountsSeparate: accountsSeparate === 'true',
          businessEntity,
          taxEntityType: taxEntityValue,
          fileInternationalTaxes: fileInternationalTaxes === 'true',
          approximatePracticeStartDate,
          practiceType:
            numberOfEmployees === NumberOfEmployees.justMe
              ? PracticeType.solo
              : PracticeType.group,
          practiceSize: numberOfEmployees,
        })(dispatch)

        if (selectedInstitutions?.length) {
          sessionStorage.setItem(
            'signupInstitutionSelections',
            JSON.stringify(selectedInstitutions)
          )
        } else {
          sessionStorage.removeItem('signupInstitutionSelections')
        }

        // Heard Lite is only offered to solo, non-S-corp practices.
        // Group practices and S-corps skip the Lite vs Essential selection
        // and go straight to the pricing page.
        const taxEntityStr = taxEntityType as string
        const isScorp =
          taxEntityStr === TAX_ENTITY_TYPES.form_1120_s ||
          taxEntityStr === TAX_ENTITY_TYPES.form_1120
        const isGroup = numberOfEmployees !== NumberOfEmployees.justMe

        if (isScorp || isGroup) {
          // If the user previously selected Lite then changed their answers
          // to become an S-corp or group, clear the stale localStorage
          // selection so the Payment page doesn't try to filter to an empty
          // Lite set.
          localStorage.removeItem('planTier')
          // With the catalog on, two or more plans still need the chooser; a failed or
          // short plan list keeps today's branch.
          const catalog = catalogFlagRef.current === true
          const plans = catalog
            ? await fetchSignupPlans()(dispatch).catch(() => undefined)
            : undefined
          const chooserShown = showsChooser({
            bypassCohort: true,
            catalog,
            plans,
          })
          // Only a settled answer is recorded: not with the flag off, and not after a failed request.
          if (catalog && plans) {
            recordChooserDecision(
              {
                id: financialProfile?.id,
                taxEntityType: taxEntityValue,
                practiceType: isGroup ? PracticeType.group : PracticeType.solo,
              },
              chooserShown
            )
          }
          if (chooserShown) {
            navigate(SIGNUP_PATHS.choosePlan)
          } else if (
            canOptIntoPreviousYearTaxes(annualTaxDetails, taxEntityValue)
          ) {
            navigate(SIGNUP_PATHS.previousYearTax)
          } else {
            navigate(SIGNUP_PATHS.payment)
          }
        } else {
          navigate(SIGNUP_PATHS.choosePlan)
        }
      }
    },
    [annualTaxDetails, dispatch, financialProfile?.id, navigate]
  )
}
